import { useEffect, useState } from 'react';
import { BellRing, ServerCog } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { actualizarSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import { pedirPermisoDeAvisos, permisoDeAvisos, type EstadoPermiso } from '@/lib/avisos';
import {
  activarAvisoConAppCerrada,
  desactivarAvisoConAppCerrada,
  estadoAvisoCerrado,
  estadoDelServidorDeAvisos,
  type EstadoAvisoCerrado,
  type EstadoServidor,
} from '@/lib/avisos-servidor';

const TEXTO_PERMISO: Record<EstadoPermiso, string> = {
  sin_soporte: 'Aquí no se pueden mostrar notificaciones. El sonido y la pantalla siguen igual.',
  sin_pedir: 'Todavía no le has dado permiso a DuoFit para mostrar notificaciones.',
  concedido: 'Permiso concedido. Se intentará mostrar la notificación al terminar cada descanso.',
  negado: 'Permiso negado. Se cambia en Ajustes del iPhone → DuoFit → Notificaciones.',
};

/**
 * Avisos del descanso (Fase 6, 0.12.0).
 *
 * Dice sin adornos qué se puede y qué no, porque lo que el iPhone permite a
 * una app web es limitado: no hay notificaciones locales programadas, así que
 * el aviso solo llega si la app sigue despierta. La opción de la pantalla
 * bloqueada la mantiene despierta con audio de verdad, y eso tiene un costo
 * que conviene saber antes de encenderla.
 */
export function AvisosDeDescanso({ activo, conAppCerrada }: { activo: boolean; conAppCerrada: boolean }) {
  const [permiso, setPermiso] = useState<EstadoPermiso>(() => permisoDeAvisos());
  const [pidiendo, setPidiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // El permiso puede cambiarse desde Ajustes del iPhone, sin pasar por aquí
  useEffect(() => {
    const revisar = () => setPermiso(permisoDeAvisos());
    document.addEventListener('visibilitychange', revisar);
    return () => document.removeEventListener('visibilitychange', revisar);
  }, []);

  async function pedir() {
    setPidiendo(true);
    setError(null);
    // Dentro del toque: Safari no concede el permiso de otra forma
    setPermiso(await pedirPermisoDeAvisos());
    setPidiendo(false);
  }

  async function cambiar(marcado: boolean) {
    setError(null);
    try {
      await actualizarSettings({ avisoConPantallaBloqueada: marcado });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <Seccion titulo="Aviso del descanso" descripcion="Lo que el iPhone deja hacer a una app web, sin promesas.">
      <Card>
        <div className="space-y-3">
          <Casilla marcada={activo} onCambio={(m) => void cambiar(m)}>
            Avisarme también con la pantalla bloqueada
          </Casilla>
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Para que el aviso llegue con el iPhone bloqueado, DuoFit tiene que seguir reproduciendo
            audio durante el descanso: es la única forma en que iOS no la suspende. A cambio,{' '}
            <strong className="font-medium text-texto">pausa tu música</strong> y{' '}
            <strong className="font-medium text-texto">suena aunque el teléfono esté en silencio</strong>.
            Con la opción apagada, el aviso se mezcla con tu música y respeta el modo silencio, pero
            solo suena mientras DuoFit está a la vista.
          </p>
          <p className="text-[12px] leading-relaxed text-texto-suave">
            El entrenamiento muestra, en la barra del descanso, si el sonido de verdad está
            corriendo. Si dice que sí, el aviso llega aunque bloquees el iPhone.
          </p>
          {conAppCerrada && (
            <p className="text-[12px] leading-relaxed text-texto-suave">
              <strong className="font-medium text-texto">
                Ahora mismo esta opción no se usa
              </strong>
              : con el aviso con la app cerrada encendido, el aviso lo manda el servidor y DuoFit
              ya no toma el reproductor durante el descanso, así que tu música sigue. Queda aquí
              como alternativa para cuando el servidor no esté disponible.
            </p>
          )}
        </div>

        <div className="mt-4 space-y-3 border-t border-borde pt-4">
          <h3 className="text-[14px] font-medium text-texto">Notificación del sistema (extra)</h3>
          <p className="text-[12px] leading-relaxed text-texto-suave">
            Son dos cosas distintas y conviene no confundirlas:{' '}
            <strong className="font-medium text-texto">el que avisa es el sonido</strong>. La
            notificación es un añadido que DuoFit pide mostrar en el momento en que el descanso
            llega a cero, y solo aparece si la app alcanzó a ejecutarse en ese instante. No es una
            alarma del sistema: el iPhone no permite que una app web programe un aviso para dentro
            de 90 segundos. Además, solo existe en la app instalada en el inicio, no en una pestaña
            de Safari.
          </p>
          <p className="text-[12px] leading-relaxed text-texto-suave">{TEXTO_PERMISO[permiso]}</p>
          {permiso === 'sin_pedir' && (
            <Button variante="secundario" icono={BellRing} disabled={pidiendo} onClick={() => void pedir()}>
              {pidiendo ? 'Un momento…' : 'Permitir notificaciones'}
            </Button>
          )}
        </div>

        <ConLaAppCerrada activo={conAppCerrada} onError={setError} />

        {error !== null && (
          <div className="mt-3">
            <Banner tono="alerta" titulo="No se pudo guardar">
              {error}
            </Banner>
          </div>
        )}
      </Card>
    </Seccion>
  );
}

const TEXTO_ESTADO: Record<EstadoAvisoCerrado, string> = {
  sin_soporte:
    'Aquí no se puede. En el iPhone hace falta abrir DuoFit desde el ícono de la pantalla de inicio: en una pestaña de Safari, Apple no permite estos avisos.',
  sin_permiso: 'Falta el permiso de avisos. Al encenderlo se te va a pedir.',
  negado:
    'El permiso está negado, así que ningún aviso puede llegar. Se cambia en Ajustes del iPhone → DuoFit → Notificaciones.',
  apagado: 'Apagado. Con la app cerrada no llega ningún aviso.',
  encendido: 'Encendido. Este iPhone está suscrito y el servidor manda el aviso a su hora.',
};

/**
 * El aviso con la app CERRADA (0.15.0), que es el único que pasa por fuera
 * del teléfono. Por eso dice, antes de encenderlo, exactamente qué sale de
 * aquí y qué no; y después, si el servidor está arriba, porque un aviso que
 * no llega no avisa de que no llegó.
 */
function ConLaAppCerrada({
  activo,
  onError,
}: {
  activo: boolean;
  onError: (mensaje: string | null) => void;
}) {
  const [estado, setEstado] = useState<EstadoAvisoCerrado | null>(null);
  const [servidor, setServidor] = useState<EstadoServidor | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    let vigente = true;
    void estadoAvisoCerrado(activo).then((e) => {
      if (vigente) setEstado(e);
    });
    return () => {
      vigente = false;
    };
  }, [activo]);

  async function cambiar(marcado: boolean) {
    onError(null);
    setTrabajando(true);
    try {
      // La suscripción primero: si el iPhone no la concede, el ajuste no se
      // enciende y la pantalla no promete un aviso que no va a llegar
      const quedo = marcado ? await activarAvisoConAppCerrada() : 'apagado';
      if (!marcado) await desactivarAvisoConAppCerrada();
      setEstado(quedo);
      await actualizarSettings({ avisoConAppCerrada: marcado && quedo === 'encendido' });
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setTrabajando(false);
    }
  }

  async function revisarServidor() {
    setTrabajando(true);
    setServidor(await estadoDelServidorDeAvisos());
    setTrabajando(false);
  }

  const sePuede = estado !== 'sin_soporte' && estado !== 'negado';

  return (
    <div className="mt-4 space-y-3 border-t border-borde pt-4">
      <h3 className="text-[14px] font-medium text-texto">Aviso con la app cerrada</h3>
      <p className="text-[12px] leading-relaxed text-texto-suave">
        Es el único aviso que necesita algo fuera de tu teléfono. Para que suene con DuoFit cerrada,
        un servidor tiene que mandarlo en el instante exacto, y para eso recibe{' '}
        <strong className="font-medium text-texto">dos cosas</strong>: la suscripción que da Apple
        para este iPhone y la hora del aviso. Ni tus comidas, ni tus pesajes, ni tus medidas, ni tu
        historial, ni tu nombre. El texto del aviso lo escribe el servidor y va cifrado, así que ni
        Apple ni Cloudflare pueden leerlo, y el registro se borra al entregarlo.
      </p>
      {sePuede && (
        <Casilla marcada={activo} onCambio={(m) => void cambiar(m)}>
          Avisarme también con la app cerrada
        </Casilla>
      )}
      <p className="text-[12px] leading-relaxed text-texto-suave">
        {estado === null ? 'Revisando…' : TEXTO_ESTADO[estado]}
      </p>
      {servidor !== null && (
        <Banner
          tono={servidor.tipo === 'arriba' ? 'exito' : 'alerta'}
          titulo={
            servidor.tipo === 'arriba'
              ? 'El servidor de avisos responde'
              : servidor.tipo === 'otra_clave'
                ? 'El servidor tiene otra clave'
                : 'El servidor de avisos no responde'
          }
        >
          {servidor.tipo === 'arriba'
            ? 'Está arriba y su clave es la que trae esta versión de la app.'
            : servidor.tipo === 'otra_clave'
              ? 'La app y el servidor no son pareja, así que cada aviso se rechaza. Hay que volver a guardar las dos claves del servidor: las dos tienen que salir de la misma generación.'
              : 'Sin él, el aviso con la app cerrada no llega. Revisa tu conexión; si sigue igual, el servidor está caído.'}
        </Banner>
      )}
      <Button
        variante="fantasma"
        icono={ServerCog}
        disabled={trabajando}
        onClick={() => void revisarServidor()}
      >
        {trabajando ? 'Revisando…' : 'Revisar el servidor de avisos'}
      </Button>
    </div>
  );
}
